import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ReportIssue,
  ExpandableNavigation,
  LightBox,
  TextSize,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

import {
  CrossplaneResourcesTableSelector,
  CrossplaneResourceGraphSelector,
  isCrossplaneAvailable,
  useResourceGraphAvailable,
  useResourcesListAvailable,
  IfCrossplaneResourceGraphAvailable,
  IfCrossplaneResourcesListAvailable,
} from '@terasky/backstage-plugin-crossplane-resources-frontend';

import { AppXrResourceProvider } from './useAppXrResource';
import { ModifyAppContent } from './ModifyAppContent';
import { AppComposedResourcesCard } from './AppComposedResourcesCard';
import { InfrastructureByEnvironmentCard } from './InfrastructureByEnvironmentCard';
import { XrStatusCard, XrConfigCard } from './XrOverviewCards';
import { CloudSqlConnectCard } from './CloudSqlConnectCard';
import { MemorystoreCard } from './MemorystoreCard';
import { GkeClusterCard } from './GkeClusterCard';
import { GcsBucketCard } from './GcsBucketCard';
import { LoadBalancerCard } from './LoadBalancerCard';
import { LoadBalancerRoutingCard } from './LoadBalancerRoutingCard';
import { DnsZoneCard } from './DnsZoneCard';
import { DnsRecordCard } from './DnsRecordCard';

import {
  isPluginApplicableToEntity as isPagerDutyAvailable,
  EntityPagerDutyCard,
} from '@pagerduty/backstage-plugin';

import { EntityGithubDeploymentsCard } from '@backstage-community/plugin-github-deployments';
import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';

import {
  EntityJiraOverviewCard,
  EntityJiraQueryCard,
  isJiraAvailable,
  hasJiraQuery,
} from '@roadiehq/backstage-plugin-jira';

import { EntityBitriseContent } from '@backstage-community/plugin-bitrise';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
      <ExpandableNavigation />
      <LightBox />
      <TextSize />
      <Mermaid
        enableZoom
        config={{ themeVariables: { lineColor: '#8f8f8f' } }}
      />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    {/*
      Here you can add support for different CI/CD services, for example
      using @backstage-community/plugin-github-actions as follows:
      <EntitySwitch.Case if={isGithubActionsAvailable}>
        <EntityGithubActionsContent />
      </EntitySwitch.Case>
     */}
    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item lg={4} md={6}>
      <EntityAboutCard />
    </Grid>
    <EntitySwitch>
      <EntitySwitch.Case if={isPagerDutyAvailable}>
        <Grid item lg={4} md={6}>
          <EntityPagerDutyCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <Grid item xs={12} sm={6} md={4}>
      <EntityGithubDeploymentsCard
        environments={[
          'live-nane1-cluster',
          'live-euw3-cluster',
          'staging-nane1-cluster',
          'staging-euw1-cluster',
        ]}
      />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <EntitySwitch>
      <EntitySwitch.Case if={isJiraAvailable}>
        <Grid item md={6}>
          <EntityJiraOverviewCard />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case if={hasJiraQuery}>
        <Grid item md={6}>
          {/* This card can be used as an alternative or in addition to the overview card */}
          <EntityJiraQueryCard title="Our Custom Query" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard />
    </Grid>
    {/* Renders itself away unless the entity depends on ingested GCP resources. */}
    <Grid item md={6} xs={12}>
      <InfrastructureByEnvironmentCard />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent refreshIntervalMs={30000} />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
        <Grid item md={12}>
          <InfrastructureByEnvironmentCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    {/* <EntityLayout.Route path="/github-actions" title="GitHub Actions">
      <EntityGithubActionsContent />
    </EntityLayout.Route> */}
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
        <Grid item md={12}>
          <InfrastructureByEnvironmentCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/bitrise" title="Bitrise">
      <EntityBitriseContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const crossplaneOverviewContent = (
  <AppXrResourceProvider>
    <Grid container spacing={3} alignItems="stretch">
      {entityWarningContent}
      <Grid item md={6} xs={12}>
        <EntityAboutCard />
      </Grid>
      <Grid item md={6} xs={12}>
        <XrStatusCard />
      </Grid>
      <Grid item md={6} xs={12}>
        <XrConfigCard />
      </Grid>
      <Grid item md={6} xs={12}>
        <AppComposedResourcesCard />
      </Grid>
      <Grid item md={4} xs={12}>
        <EntityLinksCard />
      </Grid>
      <Grid item md={8} xs={12}>
        <EntityCatalogGraphCard height={400} />
      </Grid>
    </Grid>
  </AppXrResourceProvider>
);

// Our XRDs all live under *.ds25.io — ds25.io/App, gcp.m.ds25.io/Sandbox — so the
// API group of the ingested resource is what makes an entity XR-backed.
//
// Deliberately NOT spec.type (comes from customWorkloadTypes[].defaultType in
// app-config, renameable) and NOT terasky.backstage.io/crossplane-resource: the
// ingestor only sets that on its composite/claim discovery paths, and with
// crossplane.xrds.enabled=false our XRs arrive via customWorkloadTypes instead,
// so it is absent on every entity we have. Verified against staging.
const isDs25Xr = (entity: {
  metadata?: { annotations?: Record<string, string> };
}) => {
  const group =
    entity.metadata?.annotations?.[
      'terasky.backstage.io/kubernetes-resource-api-version'
    ]?.split('/')[0];
  return Boolean(group === 'ds25.io' || group?.endsWith('.ds25.io'));
};

// App XRs (kind App, ds25.io) are identified by their workload URI, not by
// spec.type: that comes from customWorkloadTypes[].defaultType in app-config and
// can be renamed there without touching code. The URI also distinguishes them
// from other XR kinds (the Sandbox wrapper's URI is .../sandboxes/...).
const isModifiableApp = (entity: {
  metadata?: { annotations?: Record<string, string> };
}) =>
  Boolean(
    entity.metadata?.annotations?.[
      'terasky.backstage.io/custom-workload-uri'
    ]?.includes('/apps/'),
  );

const CrossplaneEntityPage = () => {
  const isResourcesListAvailable = useResourcesListAvailable();
  const isResourceGraphAvailable = useResourceGraphAvailable();

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {crossplaneOverviewContent}
      </EntityLayout.Route>
      <EntityLayout.Route if={isModifiableApp} path="/modify" title="Modify">
        <ModifyAppContent />
      </EntityLayout.Route>
      <EntityLayout.Route
        if={isKubernetesAvailable}
        path="/kubernetes"
        title="Kubernetes"
      >
        <EntityKubernetesContent />
      </EntityLayout.Route>
      <EntityLayout.Route
        if={isGithubActionsAvailable}
        path="/ci"
        title="CI/CD"
      >
        <EntityGithubActionsContent />
      </EntityLayout.Route>
      <EntityLayout.Route
        if={isResourcesListAvailable}
        path="/crossplane-resources"
        title="Crossplane Resources"
      >
        <IfCrossplaneResourcesListAvailable>
          <CrossplaneResourcesTableSelector />
        </IfCrossplaneResourcesListAvailable>
      </EntityLayout.Route>
      <EntityLayout.Route
        if={isResourceGraphAvailable}
        path="/crossplane-graph"
        title="Crossplane Graph"
      >
        <IfCrossplaneResourceGraphAvailable>
          <CrossplaneResourceGraphSelector />
        </IfCrossplaneResourceGraphAvailable>
      </EntityLayout.Route>
    </EntityLayout>
  );
};

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    {/*
      Matches on the terasky.backstage.io/crossplane-resource annotation, which
      the ingestor sets for composites and claims (anything with spec.crossplane)
      and not for other custom workload types — but only on its composite/claim
      discovery paths, which we don't use. So isDs25Xr (API group) is what
      actually matches our App and Sandbox XRs; isCrossplaneAvailable is kept for
      real claims, should crossplane.claims/xrds ingestion ever be enabled.

      Wrapped because EntitySwitch passes a context object as the 2nd arg, which
      isCrossplaneAvailable would otherwise read as its annotationPrefix.
    */}
    <EntitySwitch.Case
      if={entity => isDs25Xr(entity) || isCrossplaneAvailable(entity)}
    >
      <CrossplaneEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const isService =
  (svc: string) =>
  (entity: { metadata?: { annotations?: Record<string, string> } }) =>
    entity.metadata?.annotations?.['gcp.com/service'] === svc;

const resourcePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isService('cloudsql')}>
            <Grid item xs={12}>
              <CloudSqlConnectCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={isService('memorystore')}>
            <Grid item xs={12}>
              <MemorystoreCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={isService('gke')}>
            <Grid item xs={12}>
              <GkeClusterCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={isService('gcs')}>
            <Grid item xs={12}>
              <GcsBucketCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={isService('gcp-lb')}>
            <Grid item xs={12}>
              <LoadBalancerCard />
            </Grid>
            <Grid item xs={12}>
              <LoadBalancerRoutingCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={isService('cloud-dns-zone')}>
            <Grid item xs={12}>
              <DnsZoneCard />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={isService('cloud-dns-record')}>
            <Grid item xs={12}>
              <DnsRecordCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <Grid item md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourcePage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
